// ABOUTME: Hooks to fetch entity catalog data for country configurations (HCP-104402 / HCP-108901).
// ABOUTME: useEntitiesList (overview), useEntity (single entity + options), useEntityCatalogGlobal (global block).

import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {
  Entity,
  EntityCatalogEntryResponse,
  EntityCatalogGlobal,
  EntityCatalogResponse,
  EMPTY_ENTITY_CATALOG_GLOBAL,
  ScopeLevel,
  ScopeLevelFilterValue,
  SCOPE_LEVEL_FILTER_ALL,
  LEVEL_TO_API,
  apiToLevel,
} from '../types'

export interface EntitiesListFilters {
  search?: string
  level?: ScopeLevelFilterValue
}

/** Empty wrapper used when the endpoint returns 404 — keeps `.entities`/`.global` always safe to read. */
const EMPTY_ENTITY_CATALOG_RESPONSE: EntityCatalogResponse = {
  global: EMPTY_ENTITY_CATALOG_GLOBAL,
  entities: [],
}

/** True when the (Axios) error carries a 404 response status. */
const is404 = (err: unknown): boolean =>
  (err as {response?: {status?: number}} | undefined)?.response?.status === 404

/** Maps a raw catalog entry to the FE Entity model, preserving its per-entity options. */
export const mapEntry = (entry: EntityCatalogEntryResponse): Entity => ({
  id: entry.id,
  name: entry.name,
  description: entry.description,
  applicableLevels: entry.applicableLevels
    .map((level) => apiToLevel(level))
    .filter((l): l is ScopeLevel => l !== undefined),
  options: entry.options,
})

/** Normalises the wrapper response so `entities`/`global` are always defined. Exported for testing. */
export const normalizeCatalogResponse = (
  data: EntityCatalogResponse | undefined,
): EntityCatalogResponse => ({
  global: data?.global ?? EMPTY_ENTITY_CATALOG_GLOBAL,
  entities: data?.entities ?? [],
})

const entityCatalogQuery = (countryId: string, businessLine: string) => ({
  queryKey: ['entities', countryId, businessLine, '', 'all'] as const,
  queryFn: async (): Promise<EntityCatalogResponse> => {
    try {
      const res = await api.get<EntityCatalogResponse>('/tuc-configs/entity-catalog', {
        params: {countryId, businessLine: businessLine.toUpperCase()},
      })
      return normalizeCatalogResponse(res.data)
    } catch (err) {
      if (is404(err)) {
        return EMPTY_ENTITY_CATALOG_RESPONSE
      }
      throw err
    }
  },
  staleTime: 5 * 60 * 1000,
  enabled: Boolean(countryId && businessLine),
})

export const useEntitiesList = (countryId: string, businessLine: string, filters?: EntitiesListFilters) => {
  return useQuery<EntityCatalogResponse, Error, Entity[]>({
    queryKey: ['entities', countryId, businessLine, filters?.search?.trim() ?? '', filters?.level ?? 'all'],
    queryFn: async () => {
      const params: Record<string, string> = {
        countryId,
        businessLine: businessLine.toUpperCase(),
      }
      if (filters?.search?.trim()) {
        params.search = filters.search.trim()
      }
      if (filters?.level && filters.level !== SCOPE_LEVEL_FILTER_ALL) {
        params.level = LEVEL_TO_API[filters.level]
      }
      try {
        const res = await api.get<EntityCatalogResponse>('/tuc-configs/entity-catalog', {
          params,
        })
        return normalizeCatalogResponse(res.data)
      } catch (err) {
        if (is404(err)) {
          return EMPTY_ENTITY_CATALOG_RESPONSE
        }
        throw err
      }
    },
    select: (data) => data.entities.map(mapEntry).sort((a, b) => a.name.localeCompare(b.name)),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(countryId && businessLine),
  })
}

/** Returns a single entity by ID (incl. its options). Shares the unfiltered entity-catalog cache. */
export const useEntity = (countryId: string, businessLine: string, entityId: string | undefined) => {
  return useQuery<EntityCatalogResponse, Error, Entity | undefined>({
    ...entityCatalogQuery(countryId, businessLine),
    select: (data) => {
      if (!entityId) return undefined
      const entry = data.entities.find((e) => e.id === entityId)
      return entry ? mapEntry(entry) : undefined
    },
  })
}

/** Returns the always-present `global` block. Shares the unfiltered entity-catalog cache. */
export const useEntityCatalogGlobal = (countryId: string, businessLine: string) => {
  return useQuery<EntityCatalogResponse, Error, EntityCatalogGlobal>({
    ...entityCatalogQuery(countryId, businessLine),
    select: (data) => data.global,
  })
}
